import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function CalendarView({ tasks, onClose }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [days, setDays] = useState([]);
  const [monthMilestones, setMonthMilestones] = useState([]);
  const openerRef = React.useRef(typeof document === 'undefined' ? null : document.activeElement);

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  useEffect(() => {
    generateCalendarDays();
    filterMonthMilestones();
  }, [currentMonth, tasks]);

  function generateCalendarDays() {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = new Date(firstDay);
    startDay.setDate(startDay.getDate() - startDay.getDay());
    
    const calendarDays = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDay);
      day.setDate(startDay.getDate() + i);
      calendarDays.push(day);
    }
    setDays(calendarDays);
  }

  function filterMonthMilestones() {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    
    const milestones = tasks.filter(task => {
      if (!task.due_date) return false;
      const due = new Date(task.due_date);
      return due.getFullYear() === year && due.getMonth() === month;
    });
    setMonthMilestones(milestones);
  }

  function getTasksForDay(date) {
    return tasks.filter(task => {
      if (!task.due_date) return false;
      const due = new Date(task.due_date);
      return due.toDateString() === date.toDateString();
    });
  }

  function isSameMonth(date, month) {
    return date.getMonth() === month.getMonth() && date.getFullYear() === month.getFullYear();
  }

  function isSameDay(date1, date2) {
    return date1.toDateString() === date2.toDateString();
  }

  function format(date, formatStr) {
    if (formatStr === 'MMMM yyyy') {
      return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    } else if (formatStr === 'd') {
      return date.getDate().toString();
    } else if (formatStr === 'EEEE, d MMMM') {
      return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
    }
    return date.toString();
  }

  function prevMonth() {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  }

  function nextMonth() {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent returnFocusRef={openerRef} className="flex max-h-[90vh] max-w-5xl flex-col overflow-hidden rounded-2xl p-0">
        {/* Header */}
        <DialogHeader className="border-b border-border px-6 py-4 pr-14 text-left">
          <DialogTitle>Task calendar</DialogTitle>
          <DialogDescription>This is a read-only view of tasks with due dates. Use Add Task on the roadmap to create a task.</DialogDescription>
          <div className="flex items-center gap-2">
            <button onClick={prevMonth} aria-label="Previous month" className="min-h-11 min-w-11 rounded-lg p-2 hover:bg-muted">
              <ChevronLeft className="w-5 h-5" aria-hidden="true" />
            </button>
            <span className="min-w-[120px] text-center text-sm font-semibold text-foreground" aria-live="polite">
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <button onClick={nextMonth} aria-label="Next month" className="min-h-11 min-w-11 rounded-lg p-2 hover:bg-muted">
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </DialogHeader>

        {/* Calendar grid */}
        <div
          className="flex-1 overflow-y-auto p-6"
          role="region"
          aria-label="Calendar dates and tasks"
          tabIndex={0}
        >
          {/* Week day headers */}
          <div className="grid grid-cols-7 mb-2" aria-hidden="true">
            {weekDays.map(day => (
              <div key={day} className="py-2 text-center text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <ol className="grid grid-cols-7 gap-1" aria-label={`${format(currentMonth, 'MMMM yyyy')} task dates`}>
            {days.map((day, idx) => {
              const dayTasks = getTasksForDay(day);
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isToday = isSameDay(day, new Date());

              return (
                <li
                  key={idx}
                  aria-label={format(day, 'EEEE d MMMM yyyy')}
                  className={`min-h-[80px] p-2 rounded-lg border transition-colors ${
                    !isCurrentMonth ? 'bg-muted/30 border-border text-muted-foreground' :
                    isToday ? 'bg-teal-50 dark:bg-teal-950 border-teal-500' :
                    'bg-card border-border hover:bg-muted/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-medium ${
                      isToday ? 'text-teal-700 dark:text-teal-200 font-bold' :
                      !isCurrentMonth ? 'text-muted-foreground' :
                      'text-foreground'
                    }`}>
                      {format(day, 'd')}
                    </span>
                    {isToday && (
                      <span className="text-[10px] font-bold text-teal-700 dark:text-teal-200 uppercase">Today</span>
                    )}
                  </div>

                  {/* Task indicators */}
                  <ul className="space-y-0.5" aria-label={`Tasks due ${format(day, 'd MMMM yyyy')}`}>
                    {dayTasks.slice(0, 3).map(task => (
                      <li
                        key={task.id}
                        className={`text-[10px] px-1.5 py-0.5 rounded truncate font-medium ${
                          task.level === 1 ? 'bg-slate-200 text-slate-700' :
                          task.level === 2 ? 'bg-teal-100 text-teal-700' :
                          'bg-amber-100 text-amber-700'
                        }`}
                      >
                        <span className="sr-only">{task.title}</span><span aria-hidden="true">{task.title}</span>
                      </li>
                    ))}
                    {dayTasks.length > 3 && (
                      <li className="pl-1 text-[10px] text-muted-foreground">
                        +{dayTasks.length - 3} more
                      </li>
                    )}
                  </ul>
                </li>
              );
            })}
          </ol>

          {/* Upcoming milestones summary */}
          {monthMilestones.length > 0 && (
            <div className="mt-6 border-t border-border pt-4">
              <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                <CalendarIcon className="w-4 h-4" aria-hidden="true" />
                Tasks Due This Month ({monthMilestones.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {monthMilestones
                  .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
                  .map(task => (
                    <div
                      key={task.id}
                      className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-2"
                    >
                      <div className={`w-2 h-2 rounded-full shrink-0 ${
                        task.level === 1 ? 'bg-slate-500' :
                        task.level === 2 ? 'bg-teal-500' :
                        'bg-amber-500'
                      }`} aria-hidden="true" />
                      <div className="flex-1 min-w-0">
                        <p className="truncate text-sm font-medium text-foreground">{task.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(task.due_date), 'EEEE, d MMMM')}
                        </p>
                      </div>
                      <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                        task.level === 1 ? 'bg-slate-200 text-slate-700' :
                        task.level === 2 ? 'bg-teal-100 text-teal-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        L{task.level}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
